/* eslint-disable prefer-object-spread */
/* eslint-disable no-new */

import { omtServicesStartGame } from '../Services/OMT/OMT_Init';
import { FadeLayer } from '../Elements/FadeLayer';
import { InitFlowStatusTracker } from '../InitFlowStatusTracker';
import { OMT_AssetLoader } from '../Services/OMT/OMT_AssetLoader';

/**
 * class for handling preloading and game initialization
 */
export class Preloader {
  /**
   * Phaser state init
   */
  init() {
    this._updateLoadingProgress(0);

    // set debug methods for IAP state
    G._debugChangeToIAP = () => {
      G.IAP = true; this._updateConfigsToIAP();
      console.log("DEBUG: Your have switched to 'IAP' settings");
    };
    G._debugChangeToNONIAP = () => {
      G.IAP = false; this._updateConfigToNONIAP();
      console.log("DEBUG: Your have switched to 'NO IAP' settings");
    };
  }

  /**
   * begin preloading
   */
  preload() {
    this.load.onFileComplete.add((progress) => {
      //this._updateLoadingProgress(progress);
      // sdkHandler.trigger("loading.update", {
      //   progressPercentage: options.progressPercentage
      // });
    });

    OMT_AssetLoader.getInstance().loadAssets(G.soundManager, () => {
      this._onAssetsLoaded();
    });
  }

  /**
   * update loading progress in FB Instant overlay
   * @param {number} progress
   */
  _updateLoadingProgress(progress) {
    const adjustedProgress = progress < 5 ? 5 : Math.round(progress);
    OMT.platformFunctions.setLoadingProgress(adjustedProgress);
  }

  /**
   * called on all assets loaded
   */
  async _onAssetsLoaded() {
    OMT.platformTracking.logFTUEvent('FTUAssetLoadingDone', 'RTUAssetLoadingDone');
    this.load.onFileComplete.removeAll();
    G.addTextStyles();
    InitFlowStatusTracker.getInstance().setAssetsLoaded(); // flag assets loaded
    this._checkIfReadyToInitGame();
  }

  /**
   * check if everything is loaded / initialized for launching the game
   */
  _checkIfReadyToInitGame() {
    // check if we are ready to continue with the initializing the game
    let checkInitStateIntervalId = -1;
    const checkInitState = () => {
      if (InitFlowStatusTracker.getInstance().isReadyToStartupGame()) {
        if (checkInitStateIntervalId !== -1) clearInterval(checkInitStateIntervalId);
        // continue on once init conditions met
        game.resizeGame();
        this._callLoadingCompleteTrackingEvents();

        this._initGameDataAndRunGame();
        // if (OMT.systemInfo.isCompatiblePlatform) this._initGameDataAndRunGame();
        // else this._switchToWarningState();

        return true;
      }
      return false;
    };
    // check init state now, if not ready keep checking
    if (!checkInitState()) checkInitStateIntervalId = setInterval(checkInitState, 10);
  }

  /**
   * goto the incompatible device / browser warning state
   */
  async _switchToWarningState() {
    await omtServicesStartGame();
    await this._loadLanguageJSON();
    game.state.start('IncompatibleWarning');
  }

  /**
   * update loop
   */
  update() {
    // not doing anything with update atm
  }

  /**
   * tracking events to fire on asset loading complete. NOTE: DDNA is not ready at this point
   */
  _callLoadingCompleteTrackingEvents() {
    OMT.platformTracking.logFTUEvent('FTULoadingComplete', 'RTULoadingComplete');
    OMT.platformTracking.logFTUEvent('FTUX_LoadingComplete', null);
  }

  /**
   * initialize game data and launch the game now that loading is complete
   */
  async _initGameDataAndRunGame() {
    // set fade overlay for transition
    G.fadeLayer = new FadeLayer();
    // load language file(s)
    await this._loadLanguageJSON();
    // finalize OMT service initialization
    await omtServicesStartGame();
    await G.saveState.initLoadData();
    // load language file(s)
    await this._loadLanguageJSON();
    // Initialize ad system. This was moved until after initializing the saveState for new preloading rules.
    // OMT.ads.init();
    // init the x-promo module
    // OMT.crossPromo.init();

    // init game configs and related helpers
    G.Helpers.levelDataMgr.init(G.json.levels);
    G.Helpers.mapChestsMgr.init(G.json['configs/mapChests']);
    G.Helpers.gatesMgr.init(G.json['configs/gates']);
    G.Helpers.dailyRewardMgr.init(G.OMTsettings.elements.dailyRewardCalendar);
    G.Helpers.candyDataMgr.init(G.json.candies);
    G.TreasureHunt.treasureHuntMgr.init(G.json.settings.treasureHuntConfig);
    G.json.settingsNOIAP = G.json.settings;
    G.json['configs/spinWheelsNOIAP'] = G.json['configs/spinWheels'];

    G.saveState.initMapChestData(); // Had to be after the gates are loaded...
    // await OMT.notifications.init(); // Init the message scheduler

    // set the IAP / settings state
    if (G.IAP) this._updateConfigsToIAP();
    else this._updateConfigToNONIAP();

    // await this._initTrackingSystems(); // init non-FBA tracking systems
    //this._callGameStartTrackingEvents(); // tracking calls for entryPoint / gameStart

    // await this._checkBotOptIn(); // check if we want to show the bot opt-in prompt and wait if so
    // await this._handlePayload(); // Payload check happens here
    console.log('PRELOAD COMPLETE');
    // OMT.social.sendClosedHelpRequest(() => {});
    this._switchToFirstGameScene(); // switch the user to World or Game state depending on
  }

  /**
   * init DDNA + transaction tracker
   */
  async _initTrackingSystems() {
    // initialize transaction tracker
    OMT.transactionTracking.init();

    // initialize DDNA tracking and restore incomplete level rewards
    if (G.OMTsettings.DDNA) {
      await DDNA.tracking.init(G.OMTsettings.DDNA);
      DDNA.tracking.getDataCapture().setJavaScriptInitTime(jsInitTime);
      DDNA.transactionHelper.restoreQueuedLevelCoinRewards();
    }
  }

  /**
   * call startup tracking events
   */
  _callGameStartTrackingEvents() {
    const entryPointParams = DDNA.tracking.enabled ? DDNA.entryPointDataHelper.getEntryPointTrackingParams() : {};
    // track bot messages on FBA
    const { entryPointData } = OMT.envData;
    if (entryPointData && entryPointData.entryPoint === 'botMessage') {
      const [type, version] = entryPointData.messageId.split('###');
      OMT.platformTracking.logEvent(OMT.platformTracking.Events.BotMessage, 1, { type, version });
    }

    if (G.firstTime) DDNA.tracking.collectEvent('newPlayer', Object.assign({}, entryPointParams), 0, null, false);
    DDNA.tracking.collectEvent('gameStarted', Object.assign({}, entryPointParams), 0);
    DDNA.tracking.ftuxEvent(2, 'loadingDone');
    DDNA.tracking.checkClientDeviceStatus(); // check if the client is using a new device

    // Facebook analytics tracking
    OMT.platformTracking.logSessionStartEvent();
    OMT.platformTracking.logSoundStatusEvent();
  }

  /**
   * check bot optin on game start
   */
  async _checkBotOptIn() {
    return;
    if (OMT.feature.getAskForBotOptInAtLoad()) {
      const result = await OMT.bots.canSubscribeToBot();
      if (result === OMT.bots.CAN_SUBSCRIBE_BOT_FEEDBACK.YES) {
        try {
          // await OMT.bots.subscribeToBot();
        } catch (err) {
          //
        }
      }
    }
  }

  /**
   * load the language JSON
   */
  async _loadLanguageJSON() {
    await OMT.language.loadPrimaryLanguage().catch(() => {
      console.error('ERROR: Could not load primary language json');
    });
  }

  /**
   * update game configs to IAP settings
   */
  _updateConfigsToIAP() {
    G.json.settings = G.json.settingsIAP;
    G.json['configs/spinWheels'] = G.json['configs/spinWheelsIAP'];
    G.gift.init(G.json['configs/giftsIAP']);
    G.Helpers.bubbleGiftsMgr.init(G.json['configs/bubbleGiftsIAP']);
    G.TreasureHunt.treasureHuntMgr.setConfig(G.json.settings.treasureHuntConfig);
    G.Helpers.dailyRewardMgr.init(G.OMTsettings.elements.dailyRewardCalendar);
  }

  /**
   * update game configs to NO IAP settings
   */
  _updateConfigToNONIAP() {
    G.json.settings = G.json.settingsNOIAP;
    G.json['configs/spinWheels'] = G.json['configs/spinWheelsNOIAP'];
    G.gift.init(G.json['configs/gifts']);
    G.Helpers.bubbleGiftsMgr.init(G.json['configs/bubbleGifts']);
    G.TreasureHunt.treasureHuntMgr.setConfig(G.json.settings.treasureHuntConfig);
    G.Helpers.dailyRewardMgr.init(G.OMTsettings.elements.dailyRewardCalendar);
  }

  /**
   * switch users to the 'Game' or 'World' depending if they are new / returning users
   */
  _switchToFirstGameScene() {
    if (OMT.crossPromo.allowIncrement) {
      G.saveState.incrementGameCount();
      OMT.crossPromo.allowIncrement = false;
    }
    sdkHandler.trigger('loading.completed', {
      callback: (error, user) => {
        if (error) { /* loading.completed failed, handle error */ }
        else { /* loading.completed succeeded, you can use the user object {userId, name, avatar, avatarBase64} */ }
      }
    }, this);
    if (!G.music.isPlaying && !G.saveState.data.mute) {
      // Audiosprites don't allow you to set their initial volume in play()
      G.music.play('', 0, 1, true);
      G.music.volume = 0.75;
    }

    window.pausedByAd = false;
    sdkHandler.setPauseHandler(() => {
      window.pausedByAd = true;
      this._pauseGame();
    });

    sdkHandler.setUnpauseHandler(() => {
      window.pausedByAd = false;
      G.adAndVisibilityCheck(() => {
        this._resumeGame();
      });
    });
  }

  _pauseGame() {
    G.soundManager.pause(true); // pause sound during ads
  }

  _resumeGame() {
    G.soundManager.resume(true); // resume sound after ads

    setTimeout(() => {
      try {
        if (game.sound.context.state !== 'running') {
          game.sound.context.resume()
            .then(() => {
              // console.log('ac resumed');
            })
            .catch(() => {
              console.warn('ac not resumed!');
            });
        }
      } catch (e) {
        console.log(e);
      }
    }, 1000);
  }

  /**
   * check the payload. mostly just for tracking purposes at this point.
   */
  async _handlePayload() {
    const { payload } = OMT.envData;
    const { entryPoint } = OMT.envData;

    console.log('ENTRY POINT = ', JSON.stringify(entryPoint));
    console.log('PAYLOAD = ', JSON.stringify(payload));
    if (payload) {
      G.WorldMapAppearFlow.payloadChecker.checkPayloadAtPreloader(payload);
    }
    // progress is being restored so we dont want to boot as a first time user
    if (payload && payload.additions && payload.additions.restoreProgression) {
      G.restoreProgress = true;
    }
  }
}
